const STORAGE_KEY = 'sc-app-lifecycle-v1';

export interface AppLifecycleSnapshot {
  state: 'foreground' | 'background';
  lastForegroundAt: string | null;
  lastBackgroundAt: string | null;
  visibility: DocumentVisibilityState | 'unknown';
}

let installed = false;

function nowIso(): string {
  return new Date().toISOString();
}

function readSnapshot(): AppLifecycleSnapshot {
  if (typeof window === 'undefined') {
    return {
      state: 'foreground',
      lastForegroundAt: null,
      lastBackgroundAt: null,
      visibility: 'unknown'
    };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        state: 'foreground',
        lastForegroundAt: null,
        lastBackgroundAt: null,
        visibility: document.visibilityState
      };
    }
    return JSON.parse(raw) as AppLifecycleSnapshot;
  } catch {
    return {
      state: 'foreground',
      lastForegroundAt: null,
      lastBackgroundAt: null,
      visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown'
    };
  }
}

function writeSnapshot(snapshot: AppLifecycleSnapshot): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  window.dispatchEvent(new CustomEvent('service-companion:lifecycle', { detail: snapshot }));
}

function markForeground(): void {
  writeSnapshot({
    ...readSnapshot(),
    state: 'foreground',
    lastForegroundAt: nowIso(),
    visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown'
  });
}

function markBackground(): void {
  writeSnapshot({
    ...readSnapshot(),
    state: 'background',
    lastBackgroundAt: nowIso(),
    visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown'
  });
}

export function installAppLifecycleTracking(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      markBackground();
    } else {
      markForeground();
    }
  });

  window.addEventListener('focus', markForeground);
  window.addEventListener('pageshow', markForeground);
  window.addEventListener('pagehide', markBackground);

  markForeground();
}

export function getAppLifecycleSnapshot(): AppLifecycleSnapshot {
  return readSnapshot();
}
